import type { AlertView, HierarchyTree, NodeStatus } from '@occ/contracts';
import type { Edge, Node } from 'reactflow';
import { worstAlert, worstSeverity } from './alertInfo';
import { leafColor, worstColor, type TopologyColor } from './color';
import { layoutTree } from './layout';

/**
 * Traduce el árbol de `HierarchyProvider` a nodos y aristas de React Flow.
 *
 * Fase 3 (secciones 3, 7.2 y 7.3 del spec): cada hoja cruza `status` con la
 * alerta abierta de mayor severidad para su color; sucursal y cliente heredan
 * el peor color de sus hijos y llevan además el resumen que consume su hover.
 * `alertsByNode` llega vacío por omisión para que `buildGraph.test.ts` no
 * tenga que conocer alertas si el caso que prueba no las necesita.
 *
 * Fase 5 (sección 9): las sucursales ya expandidas se pueden colapsar a mano
 * para limpiar la vista — `collapsedBranchIds` por omisión es vacío, así que
 * el comportamiento por defecto no cambia respecto de fases anteriores (toda
 * sucursal de un cliente expandido dibuja sus hojas).
 */

export type ClientNodeData = {
  kind: 'client';
  label: string;
  clientId: string;
  collapsed: boolean;
  color: TopologyColor;
  onlineCount: number;
  offlineCount: number;
  unknownCount: number;
  totalCount: number;
  branchCount: number;
  branchesWithIssues: number;
};
export type BranchNodeData = {
  kind: 'branch';
  label: string;
  clientId: string;
  branchId: string;
  /** Colapso manual (fase 5), independiente del colapso de su cliente. */
  collapsed: boolean;
  color: TopologyColor;
  onlineCount: number;
  totalCount: number;
  criticalCount: number;
  warningCount: number;
  /** Hasta 3 alias de los nodos en el peor estado de la sucursal (sección 7.2). */
  worstAliases: string[];
};
export type LeafNodeData = {
  kind: 'leaf';
  alias: string;
  status: NodeStatus;
  nodeId: string;
  clientId: string;
  branchId: string;
  color: TopologyColor;
  alert: AlertView | null;
};
export type TopologyNodeData = ClientNodeData | BranchNodeData | LeafNodeData;

export const clientNodeId = (clientId: string): string => `client:${clientId}`;
export const branchNodeId = (branchId: string): string => `branch:${branchId}`;
export const leafNodeId = (nodeId: string): string => `leaf:${nodeId}`;

type LeafSummary = {
  nodeId: string;
  alias: string;
  status: NodeStatus;
  color: TopologyColor;
  alert: AlertView | null;
};

const summarizeLeaves = (
  branch: HierarchyTree['clients'][number]['branches'][number],
  alertsByNode: ReadonlyMap<string, AlertView[]>,
): LeafSummary[] =>
  branch.nodes.map((node) => {
    const alerts = alertsByNode.get(node.id) ?? [];
    return {
      nodeId: node.id,
      alias: node.alias,
      status: node.status,
      color: leafColor(node.status, worstSeverity(alerts)),
      alert: worstAlert(alerts),
    };
  });

/** Vacío si el color agregado es sano: no hay "peor caso" que señalar. */
const worstAliasesOf = (leaves: LeafSummary[], color: TopologyColor): string[] =>
  color === 'green'
    ? []
    : leaves
        .filter((leaf) => leaf.color === color)
        .slice(0, 3)
        .map((leaf) => leaf.alias);

export function buildGraph(
  tree: HierarchyTree,
  expandedClientIds: ReadonlySet<string>,
  alertsByNode: ReadonlyMap<string, AlertView[]> = new Map(),
  collapsedBranchIds: ReadonlySet<string> = new Set(),
): { nodes: Node<TopologyNodeData>[]; edges: Edge[] } {
  const nodes: Node<TopologyNodeData>[] = [];
  const edges: Edge[] = [];

  for (const client of tree.clients) {
    const collapsed = !expandedClientIds.has(client.id);

    const branchSummaries = client.branches.map((branch) => {
      const leaves = summarizeLeaves(branch, alertsByNode);
      const color = worstColor(leaves.map((leaf) => leaf.color));
      return {
        branch,
        leaves,
        color,
        onlineCount: leaves.filter((leaf) => leaf.status === 'online').length,
        // `info` no escala color (ver `leafColor`) y por eso tampoco entra en este
        // conteo: contarlo aquí anunciaría en el hover algo que la caja no refleja.
        criticalCount: leaves.filter((leaf) => leaf.alert?.severity === 'critical').length,
        warningCount: leaves.filter((leaf) => leaf.alert?.severity === 'warning').length,
      };
    });

    const allLeaves = branchSummaries.flatMap((summary) => summary.leaves);
    const clientColor = worstColor(branchSummaries.map((summary) => summary.color));

    nodes.push({
      id: clientNodeId(client.id),
      type: 'client',
      position: { x: 0, y: 0 },
      data: {
        kind: 'client',
        label: client.name,
        clientId: client.id,
        collapsed,
        color: clientColor,
        onlineCount: allLeaves.filter((leaf) => leaf.status === 'online').length,
        offlineCount: allLeaves.filter((leaf) => leaf.status === 'offline').length,
        unknownCount: allLeaves.filter((leaf) => leaf.status === 'unknown').length,
        totalCount: allLeaves.length,
        branchCount: branchSummaries.length,
        branchesWithIssues: branchSummaries.filter(
          (summary) => summary.color === 'red' || summary.color === 'yellow',
        ).length,
      },
    });

    if (collapsed) continue;

    for (const summary of branchSummaries) {
      const branchCollapsed = collapsedBranchIds.has(summary.branch.id);
      nodes.push({
        id: branchNodeId(summary.branch.id),
        type: 'branch',
        position: { x: 0, y: 0 },
        data: {
          kind: 'branch',
          label: summary.branch.name,
          clientId: client.id,
          branchId: summary.branch.id,
          collapsed: branchCollapsed,
          color: summary.color,
          onlineCount: summary.onlineCount,
          totalCount: summary.leaves.length,
          criticalCount: summary.criticalCount,
          warningCount: summary.warningCount,
          worstAliases: worstAliasesOf(summary.leaves, summary.color),
        },
      });
      edges.push({
        id: `${clientNodeId(client.id)}->${branchNodeId(summary.branch.id)}`,
        source: clientNodeId(client.id),
        target: branchNodeId(summary.branch.id),
      });

      if (branchCollapsed) continue;

      for (const leaf of summary.leaves) {
        nodes.push({
          id: leafNodeId(leaf.nodeId),
          type: 'leaf',
          position: { x: 0, y: 0 },
          data: {
            kind: 'leaf',
            alias: leaf.alias,
            status: leaf.status,
            nodeId: leaf.nodeId,
            clientId: client.id,
            branchId: summary.branch.id,
            color: leaf.color,
            alert: leaf.alert,
          },
        });
        edges.push({
          id: `${branchNodeId(summary.branch.id)}->${leafNodeId(leaf.nodeId)}`,
          source: branchNodeId(summary.branch.id),
          target: leafNodeId(leaf.nodeId),
        });
      }
    }
  }

  return { nodes: layoutTree(nodes, edges), edges };
}
